import * as React from 'react';
import { styled } from '@mui/material';
import { getToken } from '@workiva/unify-tokens';

import {
  CircularProgress,
  type CircularProgressProps,
} from 'src/components/CircularProgress';
import {
  ListItemIcon,
  type ListItemIconProps,
} from 'src/components/ListItemIcon';
import {
  type FileModel,
  fileUploadIsIndeterminate,
  type UploadStatus,
} from 'src/components/FileUploader/fileModel';
import { UnifyIcons } from 'src/components/UnifyIcons';
import { FileIcon } from '../utils/FileIcon';

type FileListItemIconOwnProps = {
  fileStatus: UploadStatus;
  fileType: string;
  uploadPercentComplete: NonNullable<FileModel['uploadPercentComplete']>;
  metaElId?: string;
  slotProps?: {
    CircularProgress?: Partial<Omit<CircularProgressProps, 'ref'>>;
  };
};

type FileListItemIconProps = ListItemIconProps & FileListItemIconOwnProps;

const FileListItemIconRoot = styled(ListItemIcon, {
  name: 'UnifyFileListItem',
  slot: 'Icon',
  overridesResolver: (_, styles) => styles.fileIcon,
})({
  minWidth: 28,
});

export function FileListItemIcon(
  props: FileListItemIconProps
): React.ReactElement | null {
  const {
    fileStatus,
    fileType,
    metaElId,
    uploadPercentComplete,
    slotProps,
    ...rest
  } = props;

  let icon: React.ReactElement;
  switch (fileStatus) {
    case 'error':
      icon = (
        <UnifyIcons.DoNotDisturbOn
          fontSize="small"
          sx={{ color: getToken(`Core/Icon/icon-error`) }}
        />
      );
      break;
    case 'uploading':
      icon = (
        <CircularProgress
          size={20}
          aria-labelledby={metaElId}
          {...(slotProps?.CircularProgress ?? {})}
          value={uploadPercentComplete}
          variant={
            fileUploadIsIndeterminate(uploadPercentComplete)
              ? 'indeterminate'
              : 'determinate'
          }
        />
      );
      break;
    case 'done':
    default:
      icon = <FileIcon fileType={fileType} />;
      break;
  }

  return <FileListItemIconRoot {...rest}>{icon}</FileListItemIconRoot>;
}

export default FileListItemIcon;
